import { useId, useState } from "react";
import MapSelector from "@/components/SVGComponents/MapSelector";
import LocationMapModal from "@/pages/Events/components/MapModal/MapModal";
import styles from "./LocationMapField.module.scss";

interface LocationMapFieldProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  hasError?: boolean;
  hideError?: boolean;
  compact?: boolean;
  id?: string;
}

const LocationMapField = ({
  value,
  onChange,
  label = "Location",
  placeholder = "Select location from map",
  error,
  hasError = false,
  hideError = false,
  compact = false,
  id,
}: LocationMapFieldProps) => {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [isMapOpen, setIsMapOpen] = useState(false);

  const openMap = () => setIsMapOpen(true);

  return (
    <div
      className={`${styles.field} ${compact ? styles.fieldCompact : ""}`.trim()}
    >
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>

      <div
        className={`${styles.inputShell} ${
          hasError ? styles.inputShellError : ""
        }`.trim()}
      >
        <input
          id={inputId}
          readOnly
          className={styles.input}
          placeholder={placeholder}
          value={value}
          onClick={openMap}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openMap();
            }
          }}
          aria-haspopup="dialog"
        />

        <button
          aria-label="Open map to select location"
          className={styles.mapButton}
          onClick={openMap}
          type="button"
        >
          <MapSelector />
        </button>
      </div>

      {!hideError ? (
        <span
          aria-live="polite"
          className={`${styles.error} ${
            !error ? styles.errorPlaceholder : ""
          }`.trim()}
        >
          {error ?? "\u00A0"}
        </span>
      ) : null}

      {isMapOpen ? (
        <LocationMapModal
          initialAddress={value}
          isOpen={isMapOpen}
          onClose={() => setIsMapOpen(false)}
          onSelectLocation={({ name }) => {
            onChange(name);
          }}
        />
      ) : null}
    </div>
  );
};

export default LocationMapField;
